import { useCallback, useRef, useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { getTaxPayerByDocument } from '@redux/pila/pila.slice';
import { ITaxpayerResponse } from '@models/TaxPayer';
import { getDocumentTypeId } from '@utils/TypeDocuments';
import { RootState } from '@redux/rootReducer';
import { resetFieldsFromMap } from '@utils/formUtils';

type FieldMap = Record<string, string | ((data: ITaxpayerResponse) => any)>;

interface IUseTaxpayerAutofillProps {
    fieldMap: FieldMap;
    setFieldValue: (field: string, value: any) => void;
    onSuccess?: (data: ITaxpayerResponse) => void;
    onError?: (error: unknown) => void;
}

interface IUseTaxpayerAutofillReturn {
    searchTaxpayer: (documentType: string, documentNumber: string) => Promise<void>;
    loading: boolean;
}

export const useTaxpayerAutofill = ({
    fieldMap,
    setFieldValue,
    onSuccess,
    onError,
}: IUseTaxpayerAutofillProps): IUseTaxpayerAutofillReturn => {
    const dispatch = useAppDispatch();
    const taxpayerData = useAppSelector((state: RootState) => state.pila.taxpayerData);

    const lastKeyRef = useRef<string>('');
    const [loading, setLoading] = useState(false);

    const fillData = useCallback(
        (data: ITaxpayerResponse) => {
            Object.entries(fieldMap).forEach(([formField, mapper]) => {
                const value = typeof mapper === 'function' ? mapper(data) : data?.[mapper as keyof ITaxpayerResponse];
                if (value !== undefined && value !== null && value !== '') {
                    setFieldValue(formField, value);
                }
            });
        },
        [fieldMap, setFieldValue]
    );

    const searchTaxpayer = useCallback(
        async (documentType: string, documentNumber: string) => {
            if (!documentType || !documentNumber) return;

            const key = `${documentType}-${documentNumber}`;

            if (lastKeyRef.current === key) return;
            lastKeyRef.current = key;

            const type = getDocumentTypeId(documentType);

            setLoading(true);

            try {
                await dispatch(
                    getTaxPayerByDocument({
                        documentType: type,
                        document: documentNumber,
                    })
                );
            } catch (error) {
                onError?.(error);
            } finally {
                setLoading(false);
            }
        },
        [dispatch, onError]
    );

    useEffect(() => {
        if (!taxpayerData) {
            resetFieldsFromMap(fieldMap, setFieldValue);
            return;
        }

        fillData(taxpayerData);

        onSuccess?.(taxpayerData);
    }, [taxpayerData, fillData, onSuccess]);

    return {
        searchTaxpayer,
        loading,
    };
};
